import {v4 as uuid} from 'uuid';
import Delta from '@/utils/wrappers/Delta';
import momentSecondsSince from '@/utils/momentSecondsSince';
import {
  DELTA_SCREEN_ROUTE_NAME_MAPPER,
  deltaClientEvents,
} from '../../lib/delta/deltaConstants';
import LTX_GLOBAL_APP_DEVICE_INFO_LOG_SCHEMA from '@/lib/delta/delta-schemas/ltxGlobalAppDeviceInfoLog';
import {isNativeMobile} from '@/utils/getPlatform';

const DEFAULTS = {
  ACTIVE_SCREEN_PRESENTED: () => ({
    screen_presentation_id: uuid(),
    presented_at_timestamp: new Date(),
    source: '',
    tab: '',
    trigger_flow_id: '',
    trigger_flow_name: '',
  }),
  FLOW: () => ({
    flow_id: uuid(),
    flow_name: '',
    reason: '',
    result: '',
    error: '',
    campaign_id: '',
    creator_id: '',
    screen_presentation_id: '',
    source: '',
  }),
  DEVICE_INFO_EVENT: (): DeviceInfoEvent => ({
    // Nullable fields defaulted to null
    advertising_id: null,
    app_language: null,
    app_version_full: null,
    appsflyer_id: null,
    cloud_kit_account_status: null,
    device_country: null,
    device_kind: null,
    device_manufacturer: null,
    device_model: null,
    device_notifications_token: null,
    firebase_app_instance_id: null,
    firmware_id: null,
    gpu_renderer: null,
    gpu_vendor: null,
    in_low_power_mode: null,
    install_count: 1, // Required field
    install_time: null,
    installer_package_name: null,
    is_advertising_tracking_enabled: null,
    last_update_time: null,
    modest_definition_revision: null,
    network_country: null,
    opengl_es_version: null,
    os_api_level: null,
    os_version: null,
    receipt_type: null,
    store_country: null,
    subscription_tiers: null,
    targeted_ads_enabled: null,
    timezone: null,
    usage_events_disabled: null,
    user_language: null,

    // Required fields
    app_bundle_id: 'default-app-bundle-id',
    app_name: 'creators',
    app_version_code: 'default-app-version-code',
    device_info_id: uuid(), // TODO: This might be completely wrong, we need to verify with BI.
    device_timestamp: Date.now(),
    editing_session_id: null,
    event_id: uuid(),
    first_installation_id: null,
    foreground_count: 1,
    foreground_run_time: 0,
    installation_id: `default-installation-id`,
    is_subscriber: false,
    lt_id: null,
    open_project_id: null,
    platform: 'default-platform',
    platform_device_id: null,
    platform_subscription_id: null,
    platform_user_id: null,
    run_id: `run-${uuid().substring(0, 8)}`,
    session_count: 1,
    session_id: `session-${uuid().substring(0, 8)}`,
    web_subscription_id: null,
  }),
};

export type ActiveScreenPresented = {
  screen_presentation_id: string;
  presented_at_timestamp?: Date;
  source?: string;
  tab?: string;
  trigger_flow_id?: string;
  trigger_flow_name?: string;
  screen_name?: string;
};

export type Flow = {
  flow_id: string;
  flow_name: string;
  reason?: string;
  result?: string;
  error?: string;
  campaign_id?: string;
  creator_id?: string;
  screen_presentation_id: string;
  source?: string;
};

// Type definition for device info event based on the schema at
// https://github.com/Lightricks/event-schemas/blob/main/resources/schemas/analytics-global-v3/ltx_global_app_device_info_log.avsc
export type DeviceInfoEvent = {
  advertising_id: string | null;
  app_language: string | null;
  app_version_full: string | null;
  appsflyer_id: string | null;
  cloud_kit_account_status: string | null;
  device_country: string | null;
  device_kind: string | null;
  device_manufacturer: string | null;
  device_model: string | null;
  device_notifications_token: string | null;
  firebase_app_instance_id: string | null;
  firmware_id: string | null;
  gpu_renderer: string | null;
  gpu_vendor: string | null;
  in_low_power_mode: boolean | null;
  install_time: number | null;
  installer_package_name: string | null;
  is_advertising_tracking_enabled: boolean | null;
  last_update_time: number | null;
  modest_definition_revision: number | null;
  network_country: string | null;
  opengl_es_version: string | null;
  os_api_level: number | null;
  os_version: string | null;
  receipt_type: string | null;
  store_country: string | null;
  subscription_tiers: string | null;
  targeted_ads_enabled: boolean | null;
  timezone: string | null;
  usage_events_disabled: boolean | null;
  user_language: string | null;
  editing_session_id: string | null;
  first_installation_id: string | null;
  lt_id: string | null;
  open_project_id: string | null;
  platform_device_id: string | null;
  platform_subscription_id: string | null;
  platform_user_id: string | null;
  web_subscription_id: string | null;

  // Required fields
  app_bundle_id: string;
  app_name: string;
  app_version_code: string;
  device_info_id: string;
  device_timestamp: number;
  event_id: string;
  foreground_count: number;
  foreground_run_time: number;
  install_count: number;
  installation_id: string;
  is_subscriber: boolean;
  platform: string;
  run_id: string;
  session_count: number;
  session_id: string;
};

class DeltaHelper {
  activeScreenPresented: ActiveScreenPresented | null = null;

  flows: {[key: string]: Flow} = {};

  deviceInfoEvent: DeviceInfoEvent = DEFAULTS.DEVICE_INFO_EVENT();

  /**
   * maps routeName to a { screen_name, tab } object
   * returns screen_name as the given routeName and an empty string tab if no mapping found
   * @param routeName
   * @returns {*|{tab: string, screen_name}}
   */
  static getScreenNameAndTab(routeName: string): {
    screen_name: string;
    tab: string;
  } {
    return (
      DELTA_SCREEN_ROUTE_NAME_MAPPER[routeName] || {
        screen_name: routeName,
        tab: '',
      }
    );
  }

  /**
   * creates an activeScreenPresented object and sends a screen_presented event to delta
   * @param routeName - given route name
   * @param source - previous page source
   * @param data - additional data to pass to the event (overrides default data)
   * @returns {Promise<void>}
   */
  screenPresented = async (routeName: string, source: string, data = {}) => {
    const screenNameAndTab = DeltaHelper.getScreenNameAndTab(routeName);

    const eventData = {
      screen_presentation_id: uuid(),
      ...screenNameAndTab,
      source: source || '',
      trigger_flow_id: '',
      trigger_flow_name: '',
      ...data,
    };

    Delta.sendEvent(deltaClientEvents.generic.screen_presented, {
      ...eventData,
    });

    this.activeScreenPresented = {
      ...eventData,
      presented_at_timestamp: new Date(),
    };
  };

  /**
   * sends a screen_dismissed event to delta using the activeScreenPresented
   * @param routeName - given route name
   * @param destination - next page destination
   * @param data - additional data to pass to the event (overrides default data)
   * @returns {Promise<void>}
   */
  screenDismissed = async (
    routeName: string,
    destination: string,
    data = {}
  ) => {
    if (!this.activeScreenPresented) {
      return;
    }

    const screenNameAndTab = DeltaHelper.getScreenNameAndTab(routeName);

    const {screen_presentation_id, presented_at_timestamp} =
      this.activeScreenPresented;

    Delta.sendEvent(deltaClientEvents.generic.screen_dismissed, {
      screen_presentation_id,
      screen_name:
        this.activeScreenPresented.screen_name || screenNameAndTab.screen_name,
      tab: screenNameAndTab.tab,
      session_duration_since_presented: presented_at_timestamp
        ? momentSecondsSince(presented_at_timestamp).toString()
        : '',
      destination: destination || '',
      ...data,
    });
  };

  /**
   * returns the active screen
   * @returns {{trigger_flow_name: string, screen_presentation_id: *, tab: string, presented_at_timestamp: Date, source: string, trigger_flow_id: string}}
   */
  getActiveScreenPresented = () => {
    return this.activeScreenPresented || DEFAULTS.ACTIVE_SCREEN_PRESENTED();
  };

  /**
   * creates a new flow and stores it in-memory
   * @param flowName - name of the flow which also serves as an identifier for the flow in the flows object
   * @param data - additional data to pass to the flow (overrides default data)
   * @returns {*}
   */
  createFlow = (flowName: string, data = {}) => {
    this.flows[flowName] = {
      ...DEFAULTS.FLOW(),
      screen_presentation_id:
        this.getActiveScreenPresented().screen_presentation_id,
      flow_name: flowName,
      ...data,
    };

    return this.flows[flowName];
  };

  /**
   * returns a flow
   * if the flow does not exist, it will be created and returned
   * @param flowName
   */
  getOrCreateFlow = (flowName: string) => {
    return this.getFlow(flowName) || this.createFlow(flowName);
  };

  /**
   * returns a flow
   * @param flowName
   * @returns {*}
   */
  getFlow = (flowName: string) => {
    return this.flows[flowName];
  };

  /**
   *  return base attributes for GENERIC.BUTTON_PRESSED event that is triggering a flow
   * @param flowName
   * @returns {{screen_presentation_id: string, triggered_flow_id: string, creator_id: string, campaign_id: string, triggered_flow_name: string}}
   */
  getButtonPressedBaseAttributesFromFlow = (flowName: string) => {
    const flow = this.getOrCreateFlow(flowName);
    return {
      triggered_flow_id: flow.flow_id,
      triggered_flow_name: flow.flow_name,
      campaign_id: flow.campaign_id,
      creator_id: flow.creator_id,
      screen_presentation_id: flow.screen_presentation_id,
    };
  };

  /**
   * updates a flow with new given data
   * @param flowName
   * @param data - data to pass to the flow (overrides previous data)
   * @returns {*}
   */
  updateFlow = (flowName: string, data = {}) => {
    const flow = this.getOrCreateFlow(flowName);
    this.flows[flowName] = {
      ...flow,
      ...data,
    };

    return this.flows[flowName];
  };

  /**
   * check if a flow exists in-memory
   * @param flowName
   * @returns {boolean}
   */
  flowExists = (flowName: string) => {
    return !!this.flows[flowName];
  };

  /**
   * deletes flow from memory
   * @param flowName
   */
  deleteFlow = (flowName: string) => {
    delete this.flows[flowName];
  };

  /**
   * sends a flow_started event constructed from an in-memory flow
   * @param flowName
   * @param data - additional data to pass to the event (overrides default data)
   */
  startFlow = (flowName: string, data = {}) => {
    const flow = this.getOrCreateFlow(flowName);
    Delta.sendEvent(deltaClientEvents.generic.flow_started, {
      flow_id: flow.flow_id,
      flow_name: flow.flow_name || '',
      reason: flow.reason || '',
      campaign_id: flow.campaign_id || '',
      creator_id: flow.creator_id || '',
      screen_presentation_id: flow.screen_presentation_id || '',
      source: flow.source || '',
      ...data,
    });
  };

  /**
   * sends a flow_ended event constructed from an in-memory flow
   * @param flowName
   * @param data - additional data to pass to the event (overrides default data)
   * @param deleteFlowAfterSending - whether or not to delete the flow from the memory after sending - defaults to true
   */
  endFlow = (flowName: string, data = {}, deleteFlowAfterSending = true) => {
    const flow = this.getOrCreateFlow(flowName);
    Delta.sendEvent(deltaClientEvents.generic.flow_ended, {
      flow_id: flow.flow_id,
      flow_name: flow.flow_name || '',
      result: flow.result || '',
      error: flow.error || '',
      campaign_id: flow.campaign_id || '',
      creator_id: flow.creator_id || '',
      screen_presentation_id: flow.screen_presentation_id || '',
      source: flow.source || '',
      ...data,
    });

    if (deleteFlowAfterSending) {
      this.deleteFlow(flowName);
    }
  };

  /**
   * Updates the stored device info event with the provided data and sends it
   * @param overrideData - data to apply to the device info event (overrides existing data)
   * @returns {DeviceInfoEvent} - The merged and sent device info event
   */
  saveAndSendDeviceInfoLog = (overrideData: Partial<DeviceInfoEvent> = {}) => {
    // Update the stored device info event with new data
    this.deviceInfoEvent = {
      ...this.deviceInfoEvent,
      // Update dynamic fields that should refresh on each send
      device_timestamp: Date.now(),
      event_id: uuid(),
      ...overrideData,
    };

    // This event is relevant only for native mobile apps
    if (isNativeMobile()) {
      Delta.sendEvent(
        LTX_GLOBAL_APP_DEVICE_INFO_LOG_SCHEMA.name,
        this.deviceInfoEvent
      );
    }

    return this.deviceInfoEvent;
  };
}

export default new DeltaHelper();
